import type { GuessRow } from "./keystate";
import type { KeyState } from "./keystate";
import { Tile } from "./Tile";

interface GridProps {
  guesses: GuessRow[];
  current: string;
  /** Row whose reveal animation is playing (the just-submitted guess). */
  revealRow: number | null;
  shakeRow: number | null;
  bounceRow: number | null;
  finished: boolean;
  /** Tiles per row — 5 for wordle (default), 6 for mathle. */
  cols?: number;
  /** Vertical px reserved for header + controls in the height-bound formula. */
  reservedPx?: number;
}

const ROWS = 6;

export function Grid({ guesses, current, revealRow, shakeRow, bounceRow, finished, cols = 5, reservedPx = 350 }: GridProps) {
  return (
    <div
      className="mx-auto grid w-full grid-rows-6 gap-[6px] px-2"
      // Width-bound on wide screens, height-bound on short ones: 6 rows + gaps must fit
      // between the header (~52px) and the submit button + keyboard (~280px).
      style={{
        maxWidth: `min(${cols === 5 ? 350 : 390}px, calc((var(--tg-viewport-stable-height, 100vh) - ${reservedPx}px) * ${cols} / ${ROWS}))`,
      }}
    >
      {Array.from({ length: ROWS }, (_, r) => {
        const g = guesses[r];
        const isActive = r === guesses.length && !finished;
        const letters = g
          ? g.word.split("")
          : isActive
            ? current.padEnd(cols, " ").split("").map((c) => c.trim())
            : Array.from({ length: cols }, () => "");
        const states: (KeyState | null)[] = g
          ? (g.marks.split("") as KeyState[])
          : Array.from({ length: cols }, () => null);

        return (
          <div
            key={r}
            className={`grid gap-[6px] ${shakeRow === r ? "row-shake" : ""}`}
            style={{ gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))` }}
          >
            {letters.map((letter, c) => (
              <Tile
                // Remount the tile when a letter lands so the pop replays.
                key={isActive ? `${c}:${letter}` : c}
                letter={letter}
                state={states[c] ?? null}
                reveal={revealRow === r}
                bounce={bounceRow === r && revealRow !== r}
                index={c}
                pop={isActive && letter !== "" && c === current.length - 1}
              />
            ))}
          </div>
        );
      })}
    </div>
  );
}
